import createGlobe from "cobe";
import { motion } from "framer-motion";
import React, { useEffect, useRef } from "react";
import { type Locale, getTranslationFn } from "../../i18n";
import { cn } from "../../lib/utils";

interface FeaturesSectionWithBentoGridProps {
  locale: Locale;
}

export function FeaturesSectionWithBentoGrid({
  locale,
}: FeaturesSectionWithBentoGridProps) {
  const t = getTranslationFn(locale);

  const features = [
    {
      title: t("Landing.bento.dueDiligence.title"),
      description: t("Landing.bento.dueDiligence.description"),
      skeleton: <SkeletonOne />,
      className:
        "col-span-1 md:col-span-4 lg:col-span-4 border-b md:border-r border-slate-200",
    },
    {
      title: t("Landing.bento.caseResearch.title"),
      description: t("Landing.bento.caseResearch.description"),
      skeleton: <SkeletonTwo locale={locale} />,
      className:
        "col-span-1 md:col-span-2 lg:col-span-2 border-b border-slate-200",
    },
    {
      title: t("Landing.bento.reportGen.title"),
      description: t("Landing.bento.reportGen.description"),
      skeleton: <SkeletonThree />,
      className:
        "col-span-1 md:col-span-3 lg:col-span-3 border-b md:border-r border-slate-200",
    },
    {
      title: t("Landing.bento.deposition.title"),
      description: t("Landing.bento.deposition.description"),
      skeleton: <SkeletonFour />,
      className:
        "col-span-1 md:col-span-3 lg:col-span-3 border-b md:border-r border-slate-200",
    },
    {
      title: t("Landing.bento.citations.title"),
      description: t("Landing.bento.citations.description"),
      skeleton: <SkeletonFive />,
      className:
        "col-span-1 md:col-span-3 lg:col-span-3 border-b md:border-r border-slate-200",
    },
    {
      title: t("Landing.bento.auditTrail.title"),
      description: t("Landing.bento.auditTrail.description"),
      skeleton: <SkeletonSix />,
      className:
        "col-span-1 md:col-span-3 lg:col-span-3 border-b md:border-r border-slate-200",
    },
  ];
  return (
    <div className="relative z-20 pt-4 lg:pt-8 pb-10 lg:pb-20 max-w-7xl mx-auto">
      <div className="px-8 mb-12">
        <h4 className="text-3xl lg:text-5xl lg:leading-tight max-w-5xl mx-auto text-center tracking-tight font-bold text-slate-900">
          {t("Landing.bento.title")}
        </h4>

        <p className="text-sm lg:text-lg max-w-2xl my-4 mx-auto text-slate-600 text-center font-normal">
          {t("Landing.bento.description")}
        </p>
      </div>

      <div className="relative">
        <div className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-6 mt-12 border border-slate-200 overflow-hidden bg-white">
          {features.map((feature) => (
            <FeatureCard key={feature.title} className={feature.className}>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
              <div className="h-full w-full mt-4">{feature.skeleton}</div>
            </FeatureCard>
          ))}
        </div>
      </div>
    </div>
  );
}

const FeatureCard = ({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={cn(
        `p-4 sm:p-8 relative overflow-hidden bg-white hover:bg-slate-50/50 transition-colors duration-300`,
        className
      )}
    >
      {children}
    </div>
  );
};

const FeatureTitle = ({ children }: { children?: React.ReactNode }) => {
  return (
    <p className="max-w-5xl mx-auto text-left tracking-tight text-slate-900 text-xl md:text-2xl font-semibold md:leading-snug">
      {children}
    </p>
  );
};

const FeatureDescription = ({ children }: { children?: React.ReactNode }) => {
  return (
    <p
      className={cn(
        "text-sm md:text-base max-w-4xl text-left mx-auto",
        "text-slate-500 font-normal",
        "text-left mx-0 md:text-sm my-2 leading-relaxed"
      )}
    >
      {children}
    </p>
  );
};

export const SkeletonOne = () => {
  return (
    <div className="relative flex py-4 px-2 gap-10 h-full min-h-[300px]">
      <div className="w-full p-4 mx-auto bg-white border border-slate-100 rounded-lg group h-full overflow-hidden">
        <div className="flex flex-1 w-full h-full flex-col space-y-2">
          <img
            src="/assets/side-by-side.png"
            alt="Legal document analysis"
            className="h-full w-full object-cover object-left-top rounded-sm"
          />
        </div>
      </div>

      <div className="absolute bottom-0 z-40 inset-x-0 h-32 bg-gradient-to-t from-white via-white/80 to-transparent w-full pointer-events-none" />
    </div>
  );
};

export const SkeletonThree = () => {
  return (
    <div className="w-full mx-auto bg-transparent group h-full mt-4 min-h-[200px]">
      <div className="flex flex-1 w-full flex-col space-y-2 relative">
        <img
          src="/assets/raport.png"
          alt="Document summarization demo"
          className="w-full object-cover object-center rounded-lg border border-slate-100"
        />
        <div className="absolute bottom-0 z-40 inset-x-0 h-24 bg-gradient-to-t from-white via-white/60 to-transparent w-full pointer-events-none" />
      </div>
    </div>
  );
};

export const SkeletonFive = () => {
  return (
    <div className="w-full mx-auto bg-transparent group h-full mt-4 min-h-[200px]">
      <div className="flex flex-1 w-full flex-col space-y-2 relative">
        <img
          src="/assets/citation.png"
          alt="Citation demo"
          className="w-full object-cover object-center rounded-lg border border-slate-100"
        />
        <div className="absolute bottom-0 z-40 inset-x-0 h-20 bg-gradient-to-t from-white via-white/60 to-transparent w-full pointer-events-none" />
      </div>
    </div>
  );
};

export const SkeletonSix = () => {
  return (
    <div className="w-full mx-auto bg-transparent group h-full mt-4 min-h-[200px]">
      <div className="flex flex-1 w-full flex-col space-y-2 relative">
        <img
          src="/assets/transparent-plan.png"
          alt="Transparency demo"
          className="w-full object-cover object-center rounded-lg border border-slate-100"
        />
        <div className="absolute bottom-0 z-40 inset-x-0 h-24 bg-gradient-to-t from-white via-white/60 to-transparent w-full pointer-events-none" />
      </div>
    </div>
  );
};

export const SkeletonTwo = ({ locale }: { locale: Locale }) => {
  const t = getTranslationFn(locale);
  const questions = [
    t("Landing.bento.questions.q1"),
    t("Landing.bento.questions.q2"),
    t("Landing.bento.questions.q3"),
    t("Landing.bento.questions.q4"),
    t("Landing.bento.questions.q5"),
    t("Landing.bento.questions.q6"),
    t("Landing.bento.questions.q7"),
    t("Landing.bento.questions.q8"),
    t("Landing.bento.questions.q9"),
    t("Landing.bento.questions.q10"),
  ];

  const cardVariants = {
    whileHover: {
      scale: 1.05,
      rotate: 0,
      zIndex: 100,
      transition: { duration: 0.2 },
    },
  };
  return (
    <div className="relative flex flex-col items-center justify-center p-4 h-full overflow-hidden min-h-[300px]">
      <div className="flex flex-col items-center justify-center mb-4">
        {questions.slice(0, 5).map((question, idx) => (
          <motion.div
            variants={cardVariants}
            key={"question-first" + idx}
            style={{
              rotate: Math.random() * 6 - 3,
              marginLeft: idx % 2 === 0 ? "-20px" : "20px",
            }}
            whileHover="whileHover"
            className="rounded-xl -mt-4 p-3 bg-white border border-slate-200 flex-shrink-0 cursor-default hover:border-primary/30 transition-colors"
          >
            <div className="flex items-center justify-center px-2">
              <p className="text-xs text-slate-700 font-medium">{question}</p>
            </div>
          </motion.div>
        ))}
      </div>
      <div className="flex flex-col items-center justify-center">
        {questions.slice(5, 10).map((question, idx) => (
          <motion.div
            key={"question-second" + idx}
            style={{
              rotate: Math.random() * 6 - 3,
              marginLeft: idx % 2 === 0 ? "20px" : "-20px",
            }}
            variants={cardVariants}
            whileHover="whileHover"
            className="rounded-xl -mt-4 p-3 bg-white border border-slate-200 flex-shrink-0 cursor-default hover:border-primary/30 transition-colors"
          >
            <div className="flex items-center justify-center px-2">
              <p className="text-xs text-slate-700 font-medium">{question}</p>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="absolute left-0 z-[20] inset-y-0 w-12 bg-gradient-to-r from-white to-transparent h-full pointer-events-none" />
      <div className="absolute right-0 z-[20] inset-y-0 w-12 bg-gradient-to-l from-white to-transparent h-full pointer-events-none" />
      <div className="absolute bottom-0 z-[20] inset-x-0 h-24 bg-gradient-to-t from-white to-transparent w-full pointer-events-none" />
    </div>
  );
};

export const SkeletonFour = () => {
  return (
    <div className="h-full flex flex-col items-center relative bg-transparent mt-4 min-h-[200px] overflow-hidden">
      <img
        src="/assets/transcript.png"
        alt="Transcript analysis"
        className="w-[90%] aspect-square object-cover object-top rounded-lg border border-slate-100 translate-y-4"
      />
      <div className="absolute bottom-0 z-40 inset-x-0 h-32 bg-gradient-to-t from-white via-white/80 to-transparent w-full pointer-events-none" />
    </div>
  );
};

export const Globe = ({ className }: { className?: string }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    let phi = 0;

    if (!canvasRef.current) return;

    const globe = createGlobe(canvasRef.current, {
      devicePixelRatio: 2,
      width: 600 * 2,
      height: 600 * 2,
      phi: 0,
      theta: 0,
      dark: 0,
      diffuse: 1.2,
      mapSamples: 16000,
      mapBrightness: 6,
      baseColor: [1, 1, 1],
      markerColor: [0.1, 0.5, 1],
      glowColor: [0.8, 0.8, 0.8],
      markers: [
        { location: [37.7595, -122.4367], size: 0.03 },
        { location: [40.7128, -74.006], size: 0.1 },
      ],
      onRender: (state) => {
        state.phi = phi;
        phi += 0.005;
      },
    });

    return () => {
      globe.destroy();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: 600, height: 600, maxWidth: "100%", aspectRatio: 1 }}
      className={className}
    />
  );
};
